import React, { useState, useEffect, useCallback, useRef } from 'react';
import { quests as questsApi } from '../utils/api';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';

const TYPES    = ['main','side','collectible','faction'];
const POINTS   = { main:10, side:5, collectible:4, faction:3 };
const ICONS    = { main:'👑', side:'⚔️', collectible:'💎', faction:'🛡️' };
const STATUSES = ['active','completed','abandoned'];
const RECUR_TYPES = ['daily','weekly','monthly'];

const empty = { name:'', description:'', type:'side', status:'active',
                due_date:'', location:'', other_players:'', predecessor_id:'',
                is_recurring:false, recurrence_type:'weekly', recurrence_interval:1 };

function dateStr(ts) { return ts ? ts.split('T')[0] : ''; }
function todayStr()  { return new Date().toISOString().split('T')[0]; }
function isOverdue(q){ return q.status==='active' && q.due_date && dateStr(q.due_date) < todayStr(); }
function isDueToday(q){ return q.status==='active' && q.due_date && dateStr(q.due_date) === todayStr(); }

export default function QuestsPage({ activePlayerId, players }) {
  const toast = useToast();
  const [questList,  setQuestList]  = useState([]);
  const [filter,     setFilter]     = useState('active');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy,     setSortBy]     = useState('created');
  const [search,     setSearch]     = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(empty);
  const [chainData,  setChainData]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const searchRef = useRef(null);

  const load = useCallback(async () => {
    if (!activePlayerId) return;
    setLoading(true);
    try {
      const params = {};
      if (filter)     params.status = filter;
      if (typeFilter) params.type   = typeFilter;
      if (sortBy === 'due') params.sort = 'due';
      if (search.trim()) params.search = search.trim();
      setQuestList(await questsApi.list(activePlayerId, params));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activePlayerId, filter, typeFilter, sortBy, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (predecessorId) => {
    setEditing(null);
    setForm({ ...empty, predecessor_id: predecessorId||'' });
    setShowModal(true);
  };

  const openEdit = (q) => {
    setEditing(q);
    setForm({
      name:q.name, description:q.description||'', type:q.type, status:q.status,
      due_date: dateStr(q.due_date), location:q.location||'', other_players:q.other_players||'',
      predecessor_id: q.predecessor_id||'',
      is_recurring: q.is_recurring||false,
      recurrence_type: q.recurrence_type||'weekly',
      recurrence_interval: q.recurrence_interval||1,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast('Quest name is required','error'); return; }
    try {
      if (editing) {
        await questsApi.update(editing.id, { ...form, status: form.status||editing.status });
        toast('Quest updated!','success');
      } else {
        await questsApi.create({ ...form, player_id: activePlayerId });
        toast(`Quest created! +${POINTS[form.type]} pts on completion`,'success');
      }
      setShowModal(false);
      load();
    } catch (e) { toast(e.message,'error'); }
  };

  const complete = async (q) => {
    try {
      const res = await questsApi.complete(q.id);
      if (res.levelsGained > 0) {
        toast(`🎉 LEVEL UP! +${res.bonusAwarded} bonus pts! ✅ "${q.name}" +${q.points} pts`, 'success');
      } else if (res.spawned) {
        toast(`✅ Done! +${q.points} pts · Next run queued 🔄`, 'success');
      } else {
        toast(`✅ "${q.name}" complete! +${q.points} pts earned!`, 'success');
      }
      load();
    } catch (e) { toast(e.message,'error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this quest? This cannot be undone.')) return;
    try { await questsApi.delete(id); toast('Quest deleted.','info'); load(); }
    catch (e) { toast(e.message,'error'); }
  };

  const showChain = async (id) => {
    try { setChainData(await questsApi.chain(id)); }
    catch (e) { toast('Could not load quest chain','error'); }
  };

  if (!activePlayerId) return <div className="empty-state"><p>Select a player</p></div>;

  const overdueCount = questList.filter(isOverdue).length;
  const todayCount   = questList.filter(isDueToday).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">📜 Quest Log</h2>
          {filter==='active' && (overdueCount>0||todayCount>0) && (
            <div className="quest-alerts">
              {overdueCount>0 && <span className="quest-alert quest-alert-red">⚠ {overdueCount} overdue</span>}
              {todayCount>0   && <span className="quest-alert quest-alert-gold">⏰ {todayCount} due today</span>}
            </div>
          )}
        </div>
        <button className="btn btn-primary" onClick={() => openCreate()}>+ New Quest</button>
      </div>

      {/* Status tabs */}
      <div className="tabs">
        {[['active','Active'],['completed','Completed'],['abandoned','Abandoned'],['','All']].map(([s,label]) => (
          <button key={s||'all'} className={`tab${filter===s?' active':''}`} onClick={() => setFilter(s)}>
            {label}
          </button>
        ))}
      </div>

      {/* Search + type pills + sort */}
      <div className="quest-toolbar">
        <div className="quest-search-wrap">
          <input
            ref={searchRef}
            className="form-input quest-search"
            placeholder="🔍 Search quests..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="quest-search-clear" onClick={() => { setSearch(''); searchRef.current?.focus(); }}>✕</button>
          )}
        </div>
        <div className="type-filter-row">
          <button className={`type-pill${typeFilter===''?' active':''}`} onClick={() => setTypeFilter('')}>All</button>
          {TYPES.map(t => (
            <button key={t} className={`type-pill type-pill-${t}${typeFilter===t?' active':''}`}
              onClick={() => setTypeFilter(typeFilter===t?'':t)}>
              {ICONS[t]} {t}
            </button>
          ))}
        </div>
        <div className="sort-select-wrap">
          <label className="form-label" style={{ margin:0, whiteSpace:'nowrap' }}>Sort:</label>
          <select className="form-select sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="created">Newest</option>
            <option value="due">Due date</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading quests...</p></div>
      ) : questList.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📜</div>
          <p>{search ? `No quests matching "${search}"` : filter==='active' ? 'No active quests. Begin your adventure!' : 'No quests here.'}</p>
          <br />
          {!search && filter==='active' && (
            <button className="btn btn-primary" onClick={() => openCreate()}>+ Create First Quest</button>
          )}
        </div>
      ) : (
        <div className="card-grid">
          {questList.map(q => {
            const over  = isOverdue(q);
            const today = isDueToday(q);
            return (
              <div className={`card quest-card quest-card-${q.type}${over?' overdue':''}${today?' due-today':''}`} key={q.id}>
                <div className="quest-card-header">
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                    <span className={`badge badge-${q.type}`}>{ICONS[q.type]} {q.type}</span>
                    {q.is_recurring && <span className="badge badge-recurring">🔄 repeat</span>}
                  </div>
                  <div style={{ display:'flex', gap:5, alignItems:'center', flexWrap:'wrap' }}>
                    {over  && <span className="badge badge-overdue">OVERDUE</span>}
                    {today && <span className="badge badge-today">TODAY</span>}
                    <span className={`badge badge-${q.status}`}>{q.status}</span>
                  </div>
                </div>

                <h4 className="quest-card-name">{q.name}</h4>
                {q.description && <p className="quest-card-desc">{q.description}</p>}

                <div className="quest-card-meta">
                  <span className="quest-pts" style={{ color:`var(--quest-${q.type})` }}>+{q.points} PTS</span>
                  {q.due_date && (
                    <span className={over?'quest-overdue-date':today?'quest-today-date':''}>
                      📅 {new Date(dateStr(q.due_date)+'T00:00:00').toLocaleDateString()}
                    </span>
                  )}
                  {q.location && <span>📍 {q.location}</span>}
                  {q.completed_at && q.status==='completed' && (
                    <span>✅ {new Date(q.completed_at).toLocaleDateString()}</span>
                  )}
                </div>

                {q.other_players && <div className="quest-players">👥 {q.other_players}</div>}

                {(q.predecessor_id || q.successor_id) && (
                  <button className="btn btn-sm quest-chain-btn" onClick={() => showChain(q.id)}>
                    🔗 View Chain
                  </button>
                )}

                <div className="btn-group" style={{ marginTop:12 }}>
                  {q.status==='active' && (
                    <button className="btn btn-sm btn-success" onClick={() => complete(q)}>✓ Complete</button>
                  )}
                  <button className="btn btn-sm" onClick={() => openEdit(q)}>✎ Edit</button>
                  <button className="btn btn-sm" onClick={() => openCreate(q.id)} title="Chain a follow-up quest">→ Chain</button>
                  <button className="btn btn-sm btn-danger" onClick={() => remove(q.id)}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quest Chain Modal */}
      {chainData && (
        <Modal title="⛓️ Quest Chain" onClose={() => setChainData(null)}>
          <div className="quest-chain">
            {chainData.predecessors.map(p => (
              <React.Fragment key={p.id}>
                <div className={`chain-node${p.status==='completed'?' completed':''}`}>
                  <div className="chain-node-name">{p.name}</div>
                  <div className="chain-node-status">{p.status}</div>
                </div>
                <span className="chain-arrow">→</span>
              </React.Fragment>
            ))}
            <div className="chain-node current">
              <div className="chain-node-name">{chainData.current?.name}</div>
              <div className="chain-node-status">CURRENT</div>
            </div>
            {chainData.successors.map(s => (
              <React.Fragment key={s.id}>
                <span className="chain-arrow">→</span>
                <div className={`chain-node${s.status==='completed'?' completed':''}`}>
                  <div className="chain-node-name">{s.name}</div>
                  <div className="chain-node-status">{s.status}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
          <p style={{ marginTop:10, fontFamily:'var(--font-mono)', fontSize:'0.65rem', color:'var(--text-muted)' }}>
            {chainData.predecessors.length+1+chainData.successors.length} quest(s) in this chain
          </p>
        </Modal>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <Modal
          title={editing?'✎ Edit Quest':'+ New Quest'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>
                {editing ? 'Save Changes' : 'Create Quest'}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Quest Name *</label>
            <input className="form-input" value={form.name} autoFocus
              onChange={e => setForm({...form, name:e.target.value})}
              placeholder="Name your quest..." />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={form.description}
              onChange={e => setForm({...form, description:e.target.value})}
              placeholder="Describe the objective..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.type}
                onChange={e => setForm({...form, type:e.target.value})}>
                {TYPES.map(t => <option key={t} value={t}>{ICONS[t]} {t} (+{POINTS[t]} pts)</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-input" type="date" value={form.due_date}
                onChange={e => setForm({...form, due_date:e.target.value})} />
            </div>
          </div>

          {/* Status — only when editing */}
          {editing && (
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status||editing.status}
                onChange={e => setForm({...form, status:e.target.value})}>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
              {form.status==='active' && editing.status==='completed' && (
                <div style={{ fontFamily:'var(--font-mono)', fontSize:'0.62rem', color:'var(--accent-orange)', marginTop:4 }}>
                  ⚠ Reopening this quest will clear its completion timestamp.
                </div>
              )}
            </div>
          )}

          {/* Faction: other players */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Location (optional)</label>
              <input className="form-input" value={form.location}
                onChange={e => setForm({...form, location:e.target.value})}
                placeholder="Where?" />
            </div>
            <div className="form-group">
              <label className="form-label">
                {form.type==='faction' ? '🛡️ Party Members' : 'Other Players (optional)'}
              </label>
              {players && players.length > 1 && form.type==='faction' ? (
                <select className="form-select" value={form.other_players}
                  onChange={e => setForm({...form, other_players:e.target.value})}>
                  <option value="">— Select party member —</option>
                  {players.map(p => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              ) : (
                <input className="form-input" value={form.other_players}
                  onChange={e => setForm({...form, other_players:e.target.value})}
                  placeholder="e.g. Sarah, Jake" />
              )}
            </div>
          </div>

          {/* Recurring toggle */}
          <div className="form-group">
            <label className="form-checkbox">
              <input type="checkbox" checked={form.is_recurring}
                onChange={e => setForm({...form, is_recurring:e.target.checked})} />
              Recurring quest (auto-respawns when completed)
            </label>
          </div>
          {form.is_recurring && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Repeat every</label>
                <input className="form-input" type="number" min="1"
                  value={form.recurrence_interval}
                  onChange={e => setForm({...form, recurrence_interval:parseInt(e.target.value)||1})} />
              </div>
              <div className="form-group">
                <label className="form-label">Period</label>
                <select className="form-select" value={form.recurrence_type}
                  onChange={e => setForm({...form, recurrence_type:e.target.value})}>
                  {RECUR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          )}

          {form.predecessor_id && (
            <div className="chain-notice">🔗 Chains from Quest #{form.predecessor_id}</div>
          )}
          <div className="quest-point-preview">
            <span>Reward on completion:</span>
            <span className="quest-pts" style={{ color:`var(--quest-${form.type})`, fontSize:'1rem' }}>
              +{POINTS[form.type]} {ICONS[form.type]} Points
            </span>
          </div>
        </Modal>
      )}
    </div>
  );
}